"use client"

import React, { useState } from "react"
import Image from "next/image"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { sendTelegramAlert } from "@/utils/bot"

export default function ContactSection() {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
  })

  const [errors, setErrors] = useState<{ phone?: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    
    // Xóa lỗi khi user bắt đầu nhập
    if (name === 'phone' && errors.phone) {
      setErrors((prev) => ({ ...prev, phone: undefined }))
    }
  }

  const validate = () => {
    const newErrors: { phone?: string } = {}

    // Kiểm tra phone có nhập không
    if (!formData.phone.trim()) {
      newErrors.phone = "Số điện thoại là bắt buộc"
    } else {
      // Kiểm tra định dạng số điện thoại (đơn giản, chỉ gồm số và độ dài 9-12)
      const phoneRegex = /^[0-9]{9,12}$/
      if (!phoneRegex.test(formData.phone.trim())) {
        newErrors.phone = "Số điện thoại không hợp lệ"
      }
    }

    setErrors(newErrors)

    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!validate()) return

    setIsSubmitting(true)
    
    try {
      
    await sendTelegramAlert({
    type: "info",
    title: "Thông tin liên hệ mới từ website",
    content: "Xin hãy vào Telegram để xem chi tiết",
    metadata: {
     ...formData
    },
  })
      
      // Reset form
      setFormData({
        fullName: "",
        phone: "",
        email: "",
        message: "",
      })
      
      // Show success alert
      setShowSuccessAlert(true)
      
      // Auto hide alert after 5 seconds
      setTimeout(() => {
        setShowSuccessAlert(false)
      }, 5000)
      
    } catch (error) {
      setErrors({ phone: "Đã xảy ra lỗi khi gửi thông tin. Vui lòng thử lại sau." })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="bg-white max-w-7xl mx-auto">
      {/* Hero Image */}
      <div className="w-full relative overflow-hidden">
        <Image
          src={require("@/assets/section/section-9.webp")}
          alt="Warehouse with trucks"
          width={1920}
          className="w-full h-[400px] object-cover object-center"
          priority
        />
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column: Company Info */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4">
              CÔNG TY CỔ PHẦN SỢI CỬU LONG
            </h2>
            <ul className="space-y-3 text-gray-700 list-disc list-inside">
              <li>
                <span className="font-semibold">Địa chỉ ĐKKD:</span> 339/3/18 Đường Liên Phường, Phường Long Trường, Thành phố Hồ Chí Minh, Việt Nam.
              </li>

              <li>
                <span className="font-semibold">Địa chỉ nhà máy:</span> Cụm Công nghiệp Dốc 47, P. Tam Phước, Tỉnh Đồng Nai, Việt Nam.
              </li>

              <li>
                <span className="font-semibold">Điện thoại:</span> 0902 855 892 / 0967 291 585
              </li>
              <li>
                <span className="font-semibold">Email:</span> info@soicuulong.vn
              </li>
              <li>
                <span className="font-semibold">Website:</span> soicuulong.vn
              </li>
            </ul>
          </div>

          {/* Right Column: Contact Form */}
          <div className="bg-white p-8 rounded-lg shadow-md">
            {/* Success Alert */}
            {showSuccessAlert && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md relative">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <strong>Thành công!</strong> Cảm ơn bạn đã liên hệ. Chúng tôi sẽ phản hồi trong thời gian sớm nhất.
                </div>
                <button
                  onClick={() => setShowSuccessAlert(false)}
                  className="absolute top-2 right-2 text-green-500 hover:text-green-700"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )}
            
            <h3 className="text-2xl font-bold text-primary mb-6">Nhận tư vấn miễn phí từ chúng tôi</h3>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <Input
                type="text"
                name="fullName"
                placeholder="Họ tên của bạn..."
                className="w-full p-3 border rounded-md"
                value={formData.fullName}
                onChange={handleChange}
              />
              
              <div className="space-y-1">
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Số điện thoại... *"
                  className={`w-full p-3 border rounded-md ${errors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>
              
              <Input
                type="email"
                name="email"
                placeholder="Địa chỉ email"
                className="w-full p-3 border rounded-md"
                value={formData.email}
                onChange={handleChange}
              />
              <Textarea
                name="message"
                placeholder="Nội dung liên hệ..."
                rows={5}
                className="w-full p-3 border rounded-md resize-none"
                value={formData.message}
                onChange={handleChange}
              />
              <Button 
                type="submit" 
                className="w-full bg-primary text-[#555555] font-bold py-3 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? "ĐANG GỬI..." : "ĐĂNG KÝ"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}